import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Send, Plus, Search, Pencil, Trash2, BarChart3, ArrowLeft, ArrowRight,
  Mail, Users, FileText, CheckCircle2, Loader2, AlertCircle, X, Check,
  Filter, SlidersHorizontal, Pause, Play, XCircle, Clock, Zap, RefreshCw,
  ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, Settings2,
  TrendingUp, CalendarClock, Flame, Thermometer,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAppearance } from "../contexts/AppearanceContext";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartTooltip, ResponsiveContainer } from "recharts";
import DealFilterModal from "./email/DealFilterModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function getHeaders() {
  const token = localStorage.getItem("session_token") || document.cookie.replace(/(?:(?:^|.*;\s*)session_token\s*=\s*([^;]*).*$)|^.*$/, "$1");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

const STATUS_MAP = {
  rascunho:  { label: "Rascunho",  color: "bg-slate-100 text-slate-600" },
  agendada:  { label: "Agendada",  color: "bg-blue-100 text-blue-700" },
  enviando:  { label: "Enviando",  color: "bg-amber-100 text-amber-700" },
  pausada:   { label: "Pausada",   color: "bg-orange-100 text-orange-700" },
  concluida: { label: "Concluída", color: "bg-emerald-100 text-emerald-700" },
  cancelada: { label: "Cancelada", color: "bg-slate-100 text-slate-500" },
  falhou:    { label: "Falhou",    color: "bg-red-100 text-red-700" },
};

const STEPS = [
  { label: "Configuração", icon: Pencil },
  { label: "Modelo", icon: FileText },
  { label: "Destinatários", icon: Users },
  { label: "Revisar e Enviar", icon: Send },
];

const CAMPAIGN_VARIABLES = [
  { key: "nome", label: "Nome", origin: "Nome do contato" },
  { key: "email", label: "E-mail", origin: "E-mail principal do contato" },
  { key: "empresa", label: "Empresa", origin: "Empresa vinculada ao contato" },
  { key: "telefone", label: "Telefone", origin: "Telefone do contato" },
];

const DEAL_VARIABLES = [
  { key: "negocio_nome", label: "Nome do negócio", origin: "Título do negócio associado" },
  { key: "negocio_valor", label: "Valor", origin: "Valor do negócio associado" },
  { key: "negocio_status", label: "Status", origin: "Status atual do negócio" },
  { key: "negocio_pipeline", label: "Pipeline", origin: "Pipeline do negócio" },
  { key: "negocio_estagio", label: "Estágio", origin: "Estágio atual do negócio" },
];

// ── Variable Button (inline with label) ───────────────────────────────────────
function VariableButton({ tc500, tc50, onInsert, inputRef }) {
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);
  const [customFields, setCustomFields] = useState({ contatos: [], negocios: [] });

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [cRes, nRes] = await Promise.all([
          fetch(`${API}/custom-fields?module=contatos`, { credentials: "include" }),
          fetch(`${API}/custom-fields?module=negocios`, { credentials: "include" }),
        ]);
        const contatos = cRes.ok ? (await cRes.json()).map(f => ({
          key: `custom_${f.field_name}`, label: f.field_label, origin: `Campo personalizado de contato (${f.field_type})`,
        })) : [];
        const negocios = nRes.ok ? (await nRes.json()).map(f => ({
          key: `negocio_custom_${f.field_name}`, label: f.field_label, origin: `Campo personalizado de negócio (${f.field_type})`,
        })) : [];
        setCustomFields({ contatos, negocios });
      } catch { /* ignore */ }
    })();
  }, [open]);

  useEffect(() => {
    const handle = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const insert = (key, label) => {
    const el = inputRef?.current;
    if (el) {
      const chip = document.createElement("span");
      chip.contentEditable = "false";
      chip.dataset.var = key;
      chip.setAttribute("style", `display:inline-flex;align-items:center;padding:1px 7px;margin:0 1px;border-radius:10px;font-size:11px;font-weight:600;background:${tc50};color:${tc500};border:1px solid ${tc500}30;cursor:default;user-select:all;vertical-align:baseline;line-height:16px;`);
      chip.textContent = label;
      el.appendChild(chip);
      const space = document.createTextNode("\u00A0");
      el.appendChild(space);
      el.dispatchEvent(new Event("input", { bubbles: true }));
      el.focus();
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={popRef}>
      <button type="button" onClick={() => setOpen(!open)}
        className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 hover:text-slate-600 transition-colors whitespace-nowrap"
        data-testid="insert-variable-btn"
      >
        <Plus className="w-3 h-3" /> Variáveis
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 w-52 max-h-60 overflow-y-auto animate-in fade-in-0 zoom-in-95" style={{ zIndex: 9999 }}>
          <p className="px-3 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Contato</p>
          {CAMPAIGN_VARIABLES.map(v => (
            <Tooltip key={v.key}>
              <TooltipTrigger asChild>
                <button onClick={() => insert(v.key, v.label)}
                  className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
                  data-testid={`var-${v.key}`}
                >
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ backgroundColor: tc50, color: tc500 }}>{v.label}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs">{v.origin}</TooltipContent>
            </Tooltip>
          ))}
          {customFields.contatos.length > 0 && customFields.contatos.map(v => (
            <Tooltip key={v.key}>
              <TooltipTrigger asChild>
                <button onClick={() => insert(v.key, v.label)}
                  className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
                  data-testid={`var-${v.key}`}
                >
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ backgroundColor: tc50, color: tc500 }}>{v.label}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs">{v.origin}</TooltipContent>
            </Tooltip>
          ))}
          <p className="px-3 py-1 mt-1 border-t border-slate-100 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Negócio</p>
          {DEAL_VARIABLES.map(v => (
            <Tooltip key={v.key}>
              <TooltipTrigger asChild>
                <button onClick={() => insert(v.key, v.label)}
                  className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
                  data-testid={`var-${v.key}`}
                >
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ backgroundColor: tc50, color: tc500 }}>{v.label}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs">{v.origin}</TooltipContent>
            </Tooltip>
          ))}
          {customFields.negocios.length > 0 && customFields.negocios.map(v => (
            <Tooltip key={v.key}>
              <TooltipTrigger asChild>
                <button onClick={() => insert(v.key, v.label)}
                  className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
                  data-testid={`var-${v.key}`}
                >
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ backgroundColor: tc50, color: tc500 }}>{v.label}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs">{v.origin}</TooltipContent>
            </Tooltip>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Variable Input (visual chips, no button) ──────────────────────────────────
const VariableInput = React.forwardRef(function VariableInput({ value, onChange, placeholder, tc500, tc50, dataTestId }, fwdRef) {
  const localRef = useRef(null);
  const elRef = fwdRef || localRef;

  const chipStyle = `display:inline-flex;align-items:center;padding:1px 7px;margin:0 1px;border-radius:10px;font-size:11px;font-weight:600;background:${tc50};color:${tc500};border:1px solid ${tc500}30;cursor:default;user-select:all;vertical-align:baseline;line-height:16px;`;

  const valueToHtml = useCallback((val) => {
    if (!val) return "";
    return val.replace(/\{\{(\w+)\}\}/g, (_, key) => {
      const v = CAMPAIGN_VARIABLES.find(v => v.key === key);
      return `<span contenteditable="false" data-var="${key}" style="${chipStyle}">${v?.label || key}</span>`;
    });
  }, [chipStyle]);

  const htmlToValue = useCallback((el) => {
    let result = "";
    el.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) { result += node.textContent; }
      else if (node.dataset?.var) { result += `{{${node.dataset.var}}}`; }
      else { result += node.textContent; }
    });
    return result;
  }, []);

  useEffect(() => {
    const el = typeof elRef === "function" ? null : elRef?.current;
    if (el && document.activeElement !== el) {
      el.innerHTML = valueToHtml(value);
    }
  }, [value, valueToHtml, elRef]);

  const handleInput = () => {
    const el = typeof elRef === "function" ? null : elRef?.current;
    if (el) onChange(htmlToValue(el));
  };

  return (
    <div
      ref={elRef}
      contentEditable
      suppressContentEditableWarning
      data-testid={dataTestId}
      onInput={handleInput}
      data-placeholder={placeholder}
      className="variable-input min-h-[36px] max-h-[36px] overflow-hidden px-3 py-[7px] rounded-md border border-input bg-white text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      style={{ lineHeight: "20px" }}
    />
  );
});

// ── Timeline Stepper ──────────────────────────────────────────────────────────
function StepTimeline({ step, tc500, tc50 }) {
  return (
    <div className="flex items-center w-full px-2" data-testid="campaign-wizard-timeline">
      {STEPS.map((s, i) => {
        const done = i < step;
        const active = i === step;
        const Icon = s.icon;
        return (
          <div key={i} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                  done ? "text-white shadow-sm" : active ? "text-white shadow-md" : "bg-slate-100 text-slate-400"
                }`}
                style={(done || active) ? { backgroundColor: tc500 } : undefined}
                data-testid={`wizard-step-indicator-${i}`}
              >
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={`text-[11px] font-medium whitespace-nowrap ${
                (done || active) ? "text-slate-800" : "text-slate-400"
              }`}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className="flex-1 h-0.5 mx-3 mt-[-18px] rounded-full transition-colors"
                style={{ backgroundColor: i < step ? tc500 : "#e2e8f0" }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Campaign Wizard Modal ─────────────────────────────────────────────────────
function CampaignWizardModal({ open, onOpenChange, campaign, templates, onSave, onSend, onDone, tc500, tc50 }) {
  const calcInitialStep = (c) => {
    if (!c) return 0;
    if (c.recipients?.length > 0) return 3;
    if (c.template_id) return 2;
    if (c.name && c.subject) return 1;
    return 0;
  };

  const [step, setStep] = useState(calcInitialStep(campaign));
  const subjectRef = useRef(null);
  const [name, setName] = useState(campaign?.name || "");
  const [subject, setSubject] = useState(campaign?.subject || "");
  const [senderEmail, setSenderEmail] = useState(campaign?.sender_email || "");
  const [replyTo, setReplyTo] = useState(campaign?.reply_to || "");
  const [templateId, setTemplateId] = useState(campaign?.template_id || "");
  const [recipients, setRecipients] = useState(campaign?.recipients || []);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [currentCampaign, setCurrentCampaign] = useState(campaign);
  const [sendMode, setSendMode] = useState(() => campaign?.status === "agendada" && campaign?.scheduled_at ? "schedule" : "now");
  const [scheduleDate, setScheduleDate] = useState(() => {
    if (campaign?.scheduled_at) { try { return new Date(campaign.scheduled_at).toISOString().split("T")[0]; } catch {} }
    return "";
  });
  const [scheduleTime, setScheduleTime] = useState(() => {
    if (campaign?.scheduled_at) { try { const d = new Date(campaign.scheduled_at); return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`; } catch {} }
    return "10:00";
  });
  const [bestSendTime, setBestSendTime] = useState(null);

  // Contacts
  const [emailErrors, setEmailErrors] = useState({});
  const [contacts, setContacts] = useState([]);
  const validateEmail = (val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
  const handleEmailChange = (field, setter) => (e) => { setter(e.target.value); };
  const [contactSearch, setContactSearch] = useState("");
  const [loadingContacts, setLoadingContacts] = useState(false);
  // Advanced filter
  const [showFilter, setShowFilter] = useState(false);
  const [filterApplied, setFilterApplied] = useState(false);

  const fetchContacts = useCallback(async (q = "") => {
    setLoadingContacts(true);
    try {
      const res = await fetch(`${API}/email-campaigns/contacts-with-email?search=${encodeURIComponent(q)}`, { headers: getHeaders() });
      if (res.ok) setContacts(await res.json());
    } catch { /* ignore */ }
    setLoadingContacts(false);
  }, []);

  const handleFilterApply = (filteredContacts) => {
    setContacts(filteredContacts);
    setFilterApplied(true);
  };

  const clearFilter = useCallback(() => {
    setFilterApplied(false);
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => { if (step === 2 && open) { fetchContacts(); } }, [step, open, fetchContacts]);

  // Fetch best send time when reaching step 3
  useEffect(() => {
    if (step === 3 && open) {
      fetch(`${API}/email-campaigns/best-send-time`, { headers: getHeaders() })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setBestSendTime(data); })
        .catch(() => {});
    }
  }, [step, open]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep(calcInitialStep(campaign));
      setName(campaign?.name || "");
      setSubject(campaign?.subject || "");
      setSenderEmail(campaign?.sender_email || "");
      setReplyTo(campaign?.reply_to || "");
      setTemplateId(campaign?.template_id || "");
      setRecipients(campaign?.recipients || []);
      setCurrentCampaign(campaign);
      setContactSearch("");
      setFilterApplied(false);
    }
  }, [open, campaign]);

  const selectedTemplate = templates.find(t => t.template_id === templateId);

  const canAdvance = () => {
    if (step === 0) return name.trim() && subject.trim();
    if (step === 1) return !!templateId;
    if (step === 2) return recipients.length > 0;
    return true;
  };

  const validateForm = () => {
    if (!name.trim()) { toast.error("Nome da campanha é obrigatório"); return false; }
    if (!subject.trim()) { toast.error("Assunto é obrigatório"); return false; }
    if (!validateEmail(senderEmail)) { toast.error("E-mail remetente inválido"); return false; }
    if (!validateEmail(replyTo)) { toast.error("E-mail de resposta inválido"); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const data = { name, subject, sender_email: senderEmail, reply_to: replyTo, template_id: templateId, recipients };
      const saved = await onSave(data, currentCampaign);
      if (saved) setCurrentCampaign(saved);
      toast.success("Campanha salva!");
    } catch { toast.error("Erro ao salvar."); }
    setSaving(false);
  };

  const handleSend = async () => {
    if (!validateForm()) return;
    if (!templateId) { toast.error("Selecione um modelo de e-mail"); return; }
    if (recipients.length === 0) { toast.error("Selecione pelo menos um destinatário"); return; }
    const cid = currentCampaign?.campaign_id;
    if (!cid) {
      toast.error("Salve a campanha antes de enviar.");
      return;
    }

    if (sendMode === "schedule") {
      if (!scheduleDate || !scheduleTime) { toast.error("Selecione data e hora para agendar"); return; }
      const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}:00`).toISOString();
      if (new Date(scheduledAt) <= new Date()) { toast.error("O horário de agendamento deve ser no futuro"); return; }
      setSending(true);
      try {
        // Save first then schedule
        await onSave({ name, subject, sender_email: senderEmail, reply_to: replyTo, template_id: templateId, recipients }, currentCampaign);
        const res = await fetch(`${API}/email-campaigns/${cid}/schedule`, {
          method: "POST", headers: { ...getHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ scheduled_at: scheduledAt }),
        });
        if (res.ok) {
          toast.success(`Campanha agendada para ${new Date(scheduledAt).toLocaleString("pt-BR")}`);
          onOpenChange(false);
          if (onDone) onDone();
        } else {
          const err = await res.json().catch(() => ({}));
          toast.error(err.detail || "Erro ao agendar campanha.");
        }
      } catch { toast.error("Erro ao agendar campanha."); }
      setSending(false);
      return;
    }

    setSending(true);
    try {
      const result = await onSend(cid);
      if (result?.ok) {
        onOpenChange(false);
      } else {
        toast.error("Erro ao enviar campanha.");
      }
    } catch { toast.error("Erro ao enviar campanha."); }
    setSending(false);
  };

  const silentSave = useCallback(async () => {
    const data = { name, subject, sender_email: senderEmail, reply_to: replyTo, template_id: templateId, recipients };
    if (sendMode === "schedule" && scheduleDate && scheduleTime) {
      data.scheduled_at = new Date(`${scheduleDate}T${scheduleTime}:00`).toISOString();
    } else {
      data.scheduled_at = null;
    }
    try {
      const saved = await onSave(data, currentCampaign);
      if (saved) setCurrentCampaign(saved);
    } catch { /* ignore */ }
  }, [name, subject, senderEmail, replyTo, templateId, recipients, currentCampaign, onSave, sendMode, scheduleDate, scheduleTime]);

  const handleNext = () => {
    if (step === 0) {
      if (!validateForm()) return;
    }
    silentSave();
    setStep(step + 1);
  };

  const toggleRecipient = (id) => {
    setRecipients(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    const allIds = contacts.map(c => c.id);
    const allSelected = allIds.every(id => recipients.includes(id));
    setRecipients(allSelected ? recipients.filter(id => !allIds.includes(id)) : [...new Set([...recipients, ...allIds])]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col !p-0 !gap-0" style={{ overflow: "visible" }} data-testid="campaign-wizard-modal">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-100">
          <DialogHeader className="mb-0">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
                <Send className="w-4 h-4" style={{ color: tc500 }} />
              </div>
              {currentCampaign?.campaign_id ? "Editar Campanha" : "Nova Campanha"}
            </DialogTitle>
            <DialogDescription className="text-xs">Preencha as etapas para configurar e enviar sua campanha.</DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <StepTimeline step={step} tc500={tc500} tc50={tc50} />
          </div>
        </div>

        {/* Step Content */}
        <div className="flex-1 px-6 py-5 min-h-[320px] max-h-[50vh] overflow-y-auto">
          {/* Step 0: Config */}
          {step === 0 && (
            <div className="space-y-4" data-testid="wizard-step-config">
              <div>
                <Label className="text-sm font-medium text-slate-700">Nome da campanha *</Label>
                <Input data-testid="campaign-name" value={name} onChange={e => setName(e.target.value)} className="mt-1" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-slate-700">Assunto do e-mail *</Label>
                  <VariableButton tc500={tc500} tc50={tc50} onInsert={(key) => setSubject(prev => prev + `{{${key}}}`)} inputRef={subjectRef} />
                </div>
                <div className="mt-1">
                  <VariableInput ref={subjectRef} value={subject} onChange={setSubject} tc500={tc500} tc50={tc50} dataTestId="campaign-subject" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium text-slate-700">E-mail remetente</Label>
                  <Input type="email" value={senderEmail} onChange={handleEmailChange("sender", setSenderEmail)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Responder para</Label>
                  <Input type="email" value={replyTo} onChange={handleEmailChange("reply", setReplyTo)} className="mt-1" />
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Template */}
          {step === 1 && (
            <div data-testid="wizard-step-template">
              <p className="text-sm text-slate-500 mb-4">Selecione o modelo de e-mail para esta campanha:</p>
              {templates.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">Nenhum modelo publicado</p>
                  <p className="text-slate-400 text-xs mt-1">Crie e publique um modelo em E-mail &gt; Modelos</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {templates.map(t => (
                    <button key={t.template_id} onClick={() => setTemplateId(t.template_id)}
                      data-testid={`select-template-${t.template_id}`}
                      className={`text-left rounded-xl border-2 overflow-hidden transition-all duration-150 ${
                        templateId === t.template_id ? "shadow-md" : "border-slate-200 hover:border-slate-300"
                      }`}
                      style={templateId === t.template_id ? { borderColor: tc500 } : undefined}>
                      <div className="h-28 relative overflow-hidden bg-white border-b border-slate-100">
                        {t.html ? (
                          <iframe
                            srcDoc={`<html><head><style>body{margin:0;overflow:hidden;}</style></head><body>${t.html}</body></html>`}
                            title={t.name}
                            className="absolute top-0 left-1/2 pointer-events-none border-0"
                            style={{ width: "600px", height: "800px", transform: "translateX(-50%) scale(0.25)", transformOrigin: "top center" }}
                            sandbox=""
                          />
                        ) : (
                          <div className="h-full flex items-center justify-center bg-slate-50">
                            <FileText className="w-6 h-6 text-slate-300" />
                          </div>
                        )}
                      </div>
                      <div className="p-2.5 flex items-center justify-between">
                        <p className="text-xs font-medium text-slate-700 truncate">{t.name}</p>
                        {templateId === t.template_id && (
                          <Check className="w-4 h-4 flex-shrink-0" style={{ color: tc500 }} />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Recipients */}
          {step === 2 && (
            <div data-testid="wizard-step-recipients">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-slate-500">
                  Selecione os destinatários
                  <span className="ml-2 font-medium" style={{ color: tc500 }}>{recipients.length} selecionado(s)</span>
                </p>
                <div className="flex items-center gap-2">
                  {filterApplied && (
                    <Button variant="ghost" size="sm" onClick={clearFilter} className="h-8 text-xs gap-1 text-slate-500">
                      <X className="w-3 h-3" /> Limpar filtro
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setShowFilter(true)}
                    className={`h-8 text-xs gap-1.5 ${filterApplied ? "border-blue-300 bg-blue-50 text-blue-700" : ""}`} data-testid="toggle-deal-filter">
                    <SlidersHorizontal className="w-3.5 h-3.5" /> Filtrar por negócios
                  </Button>
                </div>
              </div>

              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={contactSearch} onChange={e => { setContactSearch(e.target.value); if (!filterApplied) fetchContacts(e.target.value); }}
                  placeholder="Buscar na lista..." className="pl-9 h-9 text-sm" data-testid="contact-search" />
              </div>

              {/* Contacts table */}
              {loadingContacts ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin" style={{ color: tc500 }} />
                </div>
              ) : contacts.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">{filterApplied ? "Nenhum contato encontrado para este filtro" : "Nenhum contato com e-mail encontrado"}</p>
                  <p className="text-slate-400 text-xs mt-1">{filterApplied ? "Tente ajustar os filtros" : "Adicione contatos com e-mail no módulo Contatos"}</p>
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[240px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left w-10">
                          <Checkbox checked={contacts.length > 0 && contacts.every(c => recipients.includes(c.id))} onCheckedChange={toggleAll} data-testid="select-all-contacts" />
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Nome</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">E-mail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(contactSearch && filterApplied
                        ? contacts.filter(c => c.nome?.toLowerCase().includes(contactSearch.toLowerCase()) || c.email?.toLowerCase().includes(contactSearch.toLowerCase()))
                        : contacts
                      ).map(c => (
                        <tr key={c.id} className="border-t border-slate-50 hover:bg-slate-50/50 cursor-pointer" onClick={() => toggleRecipient(c.id)}>
                          <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                            <Checkbox checked={recipients.includes(c.id)} onCheckedChange={() => toggleRecipient(c.id)} data-testid={`select-contact-${c.id}`} />
                          </td>
                          <td className="px-4 py-2 font-medium text-slate-700">{c.nome}</td>
                          <td className="px-4 py-2 text-slate-500">{c.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Deal Filter Modal */}
              <DealFilterModal open={showFilter} onOpenChange={setShowFilter} onApply={handleFilterApply} tc500={tc500} />
            </div>
          )}

          {/* Step 3: Review & Schedule */}
          {step === 3 && (
            <div className="space-y-3" data-testid="wizard-step-review">
              {/* Resumo Card */}
              <div className="border border-slate-200 rounded-xl p-3 space-y-2.5" data-testid="review-summary">
                <h3 className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" style={{ color: tc500 }} /> Resumo da campanha
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: "Nome", value: name || "—" },
                    { label: "Assunto", value: subject || "—" },
                    { label: "Modelo", value: selectedTemplate?.name || "—" },
                    { label: "Destinatários", value: `${recipients.length} contato(s)` },
                  ].map((item, i) => (
                    <div key={i} className="px-2.5 py-1.5 bg-slate-50 rounded-lg">
                      <p className="text-[10px] text-slate-400 leading-tight">{item.label}</p>
                      <p className="text-xs font-medium text-slate-700 truncate">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Schedule Card */}
              <div className="border border-slate-200 rounded-xl p-3 space-y-2.5" data-testid="schedule-section">
                <h3 className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <CalendarClock className="w-3.5 h-3.5" style={{ color: tc500 }} /> Quando enviar?
                </h3>
                <div className="flex gap-2">
                  <button onClick={() => setSendMode("now")} data-testid="send-mode-now"
                    className={`flex-1 p-2.5 rounded-lg border-2 text-left transition-all ${sendMode === "now" ? "border-current" : "border-slate-200 hover:border-slate-300"}`}
                    style={sendMode === "now" ? { borderColor: tc500, backgroundColor: `${tc500}08` } : {}}>
                    <div className="flex items-center gap-1.5">
                      <Send className="w-3.5 h-3.5" style={{ color: sendMode === "now" ? tc500 : "#94a3b8" }} />
                      <span className="text-xs font-semibold" style={{ color: sendMode === "now" ? tc500 : "#475569" }}>Enviar agora</span>
                    </div>
                  </button>
                  <button onClick={() => setSendMode("schedule")} data-testid="send-mode-schedule"
                    className={`flex-1 p-2.5 rounded-lg border-2 text-left transition-all ${sendMode === "schedule" ? "border-current" : "border-slate-200 hover:border-slate-300"}`}
                    style={sendMode === "schedule" ? { borderColor: tc500, backgroundColor: `${tc500}08` } : {}}>
                    <div className="flex items-center gap-1.5">
                      <CalendarClock className="w-3.5 h-3.5" style={{ color: sendMode === "schedule" ? tc500 : "#94a3b8" }} />
                      <span className="text-xs font-semibold" style={{ color: sendMode === "schedule" ? tc500 : "#475569" }}>Agendar</span>
                    </div>
                  </button>
                </div>

                {sendMode === "schedule" && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] text-slate-500">Data</Label>
                        <Input type="date" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)}
                          min={new Date().toISOString().split("T")[0]} className="mt-0.5 h-8 text-xs" data-testid="schedule-date" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-500">Hora</Label>
                        <Input type="time" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)}
                          className="mt-0.5 h-8 text-xs" data-testid="schedule-time" />
                      </div>
                    </div>

                    {bestSendTime && bestSendTime.best_slots?.length > 0 && bestSendTime.confidence !== "baixa" && (
                      <div className="flex items-center gap-2 p-2 rounded-lg border border-emerald-200 bg-emerald-50" data-testid="best-time-suggestion">
                        <TrendingUp className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                        <p className="text-[10px] text-emerald-700 flex-1">
                          Melhor: <strong>{bestSendTime.best_slots[0].day}, {String(bestSendTime.best_slots[0].hour).padStart(2, "0")}h</strong> ({bestSendTime.best_slots[0].rate}%)
                        </p>
                        <button onClick={() => {
                          const now = new Date();
                          const targetDay = ["Segunda","Terça","Quarta","Quinta","Sexta","Sábado","Domingo"].indexOf(bestSendTime.best_slots[0].day);
                          const currentDay = now.getDay() === 0 ? 6 : now.getDay() - 1;
                          let diff = targetDay - currentDay;
                          if (diff <= 0) diff += 7;
                          const target = new Date(now.getTime() + diff * 86400000);
                          setScheduleDate(target.toISOString().split("T")[0]);
                          setScheduleTime(`${String(bestSendTime.best_slots[0].hour).padStart(2, "0")}:00`);
                        }} className="text-[10px] font-bold text-emerald-700 underline whitespace-nowrap" data-testid="use-suggested-time">
                          Usar
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {(!name.trim() || !subject.trim() || !templateId || recipients.length === 0) && (
                <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  Preencha todos os campos obrigatórios.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
          <Button variant="outline" size="sm" onClick={() => step > 0 ? setStep(step - 1) : onOpenChange(false)} className="gap-1" data-testid="wizard-prev-btn">
            <ArrowLeft className="w-4 h-4" /> {step > 0 ? "Anterior" : "Cancelar"}
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} className="gap-1" data-testid="save-campaign-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Salvar rascunho
            </Button>
            {step < 3 ? (
              <Button size="sm" onClick={handleNext} disabled={!canAdvance()} className="gap-1 text-white hover:opacity-90" style={{ backgroundColor: tc500 }} data-testid="wizard-next-btn">
                Próximo <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button size="sm" onClick={handleSend} disabled={sending || !name.trim() || !subject.trim() || !templateId || recipients.length === 0}
                className="gap-1 text-white hover:opacity-90" style={{ backgroundColor: tc500 }} data-testid="send-campaign-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : sendMode === "schedule" ? <CalendarClock className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                {sendMode === "schedule" ? "Agendar envio" : "Enviar campanha"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Campaign Detail (Post-send view) ──────────────────────────────────────────
function CampaignMetrics({ campaign, onBack, onRefreshList, tc500, tc50 }) {
  const [stats, setStats] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [startTime] = useState(Date.now());
  const [activeTab, setActiveTab] = useState("resumo");
  const [chartPeriod, setChartPeriod] = useState("24h");
  const [metricsConfirm, setMetricsConfirm] = useState(null); // "cancel" | "sendNow"
  const pollRef = useRef(null);
  const speedSamples = useRef([]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/email-campaigns/${campaign.campaign_id}/stats`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
        const live = data.status === "enviando";
        setIsLive(live);
        if (!live && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (onRefreshList) onRefreshList();
        }
      }
    } catch { /* ignore */ }
  }, [campaign.campaign_id, onRefreshList]);

  useEffect(() => {
    fetchStats();
    pollRef.current = setInterval(fetchStats, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchStats]);

  const handlePause = async () => {
    await fetch(`${API}/email-campaigns/${campaign.campaign_id}/pause`, { method: "POST", headers: getHeaders() });
    fetchStats();
  };
  const handleResume = async () => {
    await fetch(`${API}/email-campaigns/${campaign.campaign_id}/resume`, { method: "POST", headers: getHeaders() });
    toast.success("Retomando envio...");
    setIsLive(true);
    pollRef.current = pollRef.current || setInterval(fetchStats, 2500);
    fetchStats();
  };
  const handleCancel = async () => {
    await fetch(`${API}/email-campaigns/${campaign.campaign_id}/cancel`, { method: "POST", headers: getHeaders() });
    toast("Envio cancelado.");
    fetchStats();
  };

  const status = stats?.status || campaign.status;
  const st = STATUS_MAP[status] || STATUS_MAP.rascunho;
  const total = stats?.total_recipients || campaign.recipient_count || 0;
  const sentCount = stats?.sent ?? 0;
  const bouncedCount = stats?.bounced ?? 0;
  const pendingCount = stats?.pending ?? total;
  const progress = stats?.progress ?? 0;
  const processed = Math.min(sentCount + bouncedCount, total);

  const elapsed = (Date.now() - startTime) / 1000;
  const speed = elapsed > 2 && processed > 0 ? (processed / elapsed) : 0;
  const remaining = total - processed;
  const eta = speed > 0 ? Math.ceil(remaining / speed) : 0;
  const formatEta = (secs) => {
    if (secs <= 0) return "—";
    if (secs < 60) return `~${secs}s`;
    return `~${Math.floor(secs / 60)}min ${secs % 60}s`;
  };

  const TABS = [
    { id: "resumo", label: "Resumo", icon: BarChart3 },
    { id: "email", label: "E-mail", icon: FileText },
    { id: "destinatarios", label: "Destinatários", icon: Users },
    { id: "aberturas", label: "Aberturas", icon: Mail },
    { id: "cliques", label: "Cliques", icon: ArrowRight },
    { id: "descadastros", label: "Descadastros", icon: XCircle },
  ];

  const isCompleted = ["concluida", "cancelada", "falhou"].includes(status);

  const chartData = useMemo(() => {
    const timeline = stats?.opens_timeline || [];
    if (!timeline.length) return [];
    const now = new Date();
    let buckets = [];
    let labelFn, filterMs;

    if (chartPeriod === "24h") {
      filterMs = 24 * 60 * 60 * 1000;
      for (let i = 23; i >= 0; i--) {
        const d = new Date(now.getTime() - i * 3600000);
        buckets.push({ key: d.toISOString().slice(0, 13), label: `${String(d.getHours()).padStart(2, "0")}h`, count: 0 });
      }
      labelFn = (dt) => dt.toISOString().slice(0, 13);
    } else if (chartPeriod === "7d") {
      filterMs = 7 * 24 * 60 * 60 * 1000;
      for (let i = 6; i >= 0; i--) {
        const d = new Date(now.getTime() - i * 86400000);
        const key = d.toISOString().slice(0, 10);
        buckets.push({ key, label: d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }), count: 0 });
      }
      labelFn = (dt) => dt.toISOString().slice(0, 10);
    } else {
      filterMs = 28 * 24 * 60 * 60 * 1000;
      for (let i = 27; i >= 0; i--) {
        const d = new Date(now.getTime() - i * 86400000);
        const key = d.toISOString().slice(0, 10);
        buckets.push({ key, label: d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }), count: 0 });
      }
      labelFn = (dt) => dt.toISOString().slice(0, 10);
    }

    const cutoff = now.getTime() - filterMs;
    timeline.forEach(o => {
      if (!o.opened_at) return;
      const dt = new Date(o.opened_at);
      if (dt.getTime() < cutoff) return;
      const key = labelFn(dt);
      const bucket = buckets.find(b => b.key === key);
      if (bucket) bucket.count++;
    });

    return buckets;
  }, [stats?.opens_timeline, chartPeriod]);

  return (
    <div className="space-y-4" data-testid="campaign-metrics">
      {/* Header — padrão consistente */}
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
          <BarChart3 className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
        </div>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight">{campaign.name}</h1>
            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${st.color}`}>
              {status === "enviando" && <Loader2 className="w-3 h-3 animate-spin" />}
              {st.label}
            </span>
            {isLive && <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" /></span>}
          </div>
          <p className="text-slate-500 mt-0.5">Métricas detalhadas da campanha</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onBack} className="gap-1" data-testid="metrics-back-btn">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </Button>
          {status === "enviando" && (
            <>
              <Button variant="outline" size="sm" className="gap-1" onClick={handlePause} data-testid="pause-btn"><Pause className="w-4 h-4" /> Pausar</Button>
              <Button variant="outline" size="sm" className="gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={handleCancel} data-testid="cancel-btn"><XCircle className="w-4 h-4" /> Cancelar</Button>
            </>
          )}
          {status === "pausada" && (
            <>
              <Button size="sm" className="gap-1 text-white" style={{ backgroundColor: tc500 }} onClick={handleResume} data-testid="resume-btn"><Play className="w-4 h-4" /> Retomar</Button>
              <Button variant="outline" size="sm" className="gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={handleCancel}><XCircle className="w-4 h-4" /> Cancelar</Button>
            </>
          )}
          {(status === "cancelada" || status === "falhou") && (
            <Button size="sm" className="gap-1 text-white" style={{ backgroundColor: tc500 }} onClick={handleResume} data-testid="resend-btn"><RefreshCw className="w-4 h-4" /> Reenviar pendentes</Button>
          )}
          {status === "agendada" && (
            <>
              <Button variant="destructive" size="sm" className="gap-1" data-testid="cancel-schedule-btn"
                onClick={() => setMetricsConfirm("cancel")}>
                <XCircle className="w-4 h-4" /> Cancelar agendamento
              </Button>
              <Button size="sm" className="gap-1 text-white" style={{ backgroundColor: tc500 }} data-testid="send-now-btn"
                onClick={() => setMetricsConfirm("sendNow")}>
                <Send className="w-4 h-4" /> Enviar agora
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Progress Bar / Opens Chart / Schedule Countdown */}
      {isCompleted ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="opens-chart-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" style={{ color: tc500 }} />
              <p className="text-sm font-semibold text-slate-700">Aberturas ao longo do tempo</p>
              <span className="text-xs text-slate-400 ml-1">({stats?.total_opens ?? 0} total)</span>
            </div>
            <div className="flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="chart-period-selector">
              {[
                { id: "24h", label: "24h" },
                { id: "7d", label: "7 dias" },
                { id: "4w", label: "4 semanas" },
              ].map(p => (
                <button key={p.id} onClick={() => setChartPeriod(p.id)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    chartPeriod === p.id ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid={`chart-period-${p.id}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {chartData.length > 0 && chartData.some(d => d.count > 0) ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }}
                  interval={chartPeriod === "4w" ? 3 : chartPeriod === "7d" ? 0 : 2} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <RechartTooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                  formatter={(v) => [v, "Aberturas"]}
                  labelFormatter={(l) => chartPeriod === "24h" ? `Hora: ${l}` : `Data: ${l}`}
                />
                <Line type="monotone" dataKey="count" stroke={tc500} strokeWidth={2.5} dot={{ fill: tc500, r: 3, strokeWidth: 0 }}
                  activeDot={{ fill: tc500, r: 5, strokeWidth: 2, stroke: "#fff" }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Mail className="w-8 h-8 mb-2 text-slate-300" />
              <p className="text-sm">Nenhuma abertura registrada neste período</p>
            </div>
          )}
        </div>
      ) : status === "agendada" && campaign.scheduled_at ? (
        <div className="bg-white rounded-xl border border-blue-200 shadow-sm p-5" data-testid="schedule-countdown">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-50">
                <CalendarClock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700">Envio agendado</p>
                <p className="text-xs text-blue-600 font-medium">
                  {new Date(campaign.scheduled_at).toLocaleString("pt-BR", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-400">Tempo restante</p>
              <p className="text-lg font-bold text-blue-600">
                {(() => {
                  const diff = new Date(campaign.scheduled_at) - new Date();
                  if (diff <= 0) return "Enviando...";
                  const d = Math.floor(diff / 86400000);
                  const h = Math.floor((diff % 86400000) / 3600000);
                  const m = Math.floor((diff % 3600000) / 60000);
                  return `${d > 0 ? d + "d " : ""}${h}h ${m}min`;
                })()}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-slate-700">Progresso</p>
            <p className="text-sm font-bold" style={{ color: tc500 }}>{progress}%</p>
          </div>
          <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${progress}%`, backgroundColor: tc500 }} />
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-slate-400">{processed} de {total} processados</p>
            {isLive && speed > 0 && (
              <div className="flex items-center gap-4">
                <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Zap className="w-3 h-3" /> {speed.toFixed(1)} e/s</span>
                <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Clock className="w-3 h-3" /> {formatEta(eta)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex overflow-x-auto" style={{ backgroundColor: tc500 }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            let count = null;
            if (tab.id === "destinatarios") count = total;
            else if (tab.id === "aberturas") count = stats?.unique_opens ?? 0;
            else if (tab.id === "cliques") count = stats?.unique_clicks ?? 0;
            else if (tab.id === "descadastros") count = stats?.unsubscribes ?? 0;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                data-testid={`metrics-tab-${tab.id}`}
                className={`flex items-center gap-2 px-5 py-3.5 text-xs font-semibold transition-all whitespace-nowrap text-white ${
                  isActive ? "bg-white/20" : "hover:bg-white/10"
                }`}>
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
                {count !== null && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-white">{count}</span>}
              </button>
            );
          })}
        </div>

        <div className="p-5">
          {/* ── Tab: Resumo ── */}
          {activeTab === "resumo" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { label: "Total", value: total, icon: Users },
                  { label: "Enviados", value: sentCount, icon: CheckCircle2 },
                  { label: "Falharam", value: bouncedCount, icon: AlertCircle },
                  { label: "Aberturas", value: `${stats?.open_rate ?? 0}%`, icon: Mail },
                  { label: "Cliques", value: `${stats?.click_rate ?? 0}%`, icon: ArrowRight },
                  { label: "Descadastros", value: stats?.unsubscribes ?? 0, icon: XCircle },
                ].map((c, i) => {
                  const I = c.icon;
                  return (
                    <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                      <div className="flex items-start justify-between mb-3">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider leading-tight">{c.label}</p>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ml-2" style={{ backgroundColor: `${tc500}18` }}>
                          <I className="w-4 h-4" style={{ color: tc500 }} />
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-slate-900 leading-none">{c.value}</p>
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Assunto</p>
                  <p className="text-sm font-medium text-slate-800">{campaign.subject || "—"}</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Iniciado em</p>
                  <p className="text-sm font-medium text-slate-800">
                    {campaign.sent_at ? new Date(campaign.sent_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </p>
                </div>
              </div>
              {/* Top URLs */}
              {stats?.top_urls?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-2">Links mais clicados</p>
                  <div className="space-y-1.5">
                    {stats.top_urls.map((u, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2">
                        <span className="text-xs font-bold text-slate-400 w-5">{i + 1}.</span>
                        <p className="text-xs text-slate-600 flex-1 truncate">{u.url}</p>
                        <span className="text-xs font-bold" style={{ color: tc500 }}>{u.clicks} clique{u.clicks !== 1 ? "s" : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Tab: E-mail (Preview) ── */}
          {activeTab === "email" && (
            <div data-testid="metrics-tab-email-content">
              {stats?.template_html ? (
                <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
                  <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-500">Pré-visualização do modelo enviado</p>
                    <span className="text-[10px] text-slate-400">Apenas leitura</span>
                  </div>
                  <div className="flex justify-center bg-slate-50 p-4">
                    <iframe
                      srcDoc={stats.template_html}
                      title="Email Preview"
                      className="w-full max-w-[600px] bg-white rounded-lg border border-slate-200 shadow-sm"
                      style={{ height: "600px", border: "none" }}
                      sandbox=""
                      data-testid="email-preview-iframe"
                    />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-12">Modelo de e-mail não disponível.</p>
              )}
            </div>
          )}

          {/* ── Tab: Destinatários ── */}
          {activeTab === "destinatarios" && (
            <div>
              {stats?.sends?.length > 0 ? (
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500">E-mail</th>
                        <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500">Envio</th>
                        <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500">Aberto</th>
                        <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500">Cliques</th>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500">Erro</th>
                        <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500">Hora</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.sends.map(s => (
                        <tr key={s.send_id} className="border-t border-slate-50 hover:bg-slate-50/50">
                          <td className="px-4 py-2 text-slate-600">{s.email}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${
                              s.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                            }`}>{s.status === "sent" ? "Enviado" : "Falhou"}</span>
                          </td>
                          <td className="px-4 py-2 text-center">
                            {s.opened ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-4 py-2 text-center text-xs font-medium">{s.click_count > 0 ? <span style={{ color: tc500 }}>{s.click_count}</span> : <span className="text-slate-300">—</span>}</td>
                          <td className="px-4 py-2 text-xs text-red-500 max-w-[180px] truncate">{s.error || ""}</td>
                          <td className="px-4 py-2 text-right text-xs text-slate-400">
                            {s.sent_at ? new Date(s.sent_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-8">Nenhum envio registrado.</p>
              )}
            </div>
          )}

          {/* ── Tab: Aberturas ── */}
          {activeTab === "aberturas" && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Aberturas únicas", value: stats?.unique_opens ?? 0, icon: Mail },
                  { label: "Total de aberturas", value: stats?.total_opens ?? 0, icon: RefreshCw },
                  { label: "Taxa de abertura", value: `${stats?.open_rate ?? 0}%`, icon: BarChart3 },
                ].map((c, i) => {
                  const I = c.icon;
                  return (
                    <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                      <div className="flex items-start justify-between mb-3">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider leading-tight">{c.label}</p>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ml-2" style={{ backgroundColor: `${tc500}18` }}>
                          <I className="w-4 h-4" style={{ color: tc500 }} />
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-slate-900 leading-none">{c.value}</p>
                    </div>
                  );
                })}
              </div>
              {stats?.opens_timeline?.length > 0 ? (
                <div className="max-h-[300px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500">E-mail</th>
                        <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500">Aberto em</th>
                        <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500">Dispositivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.opens_timeline.map((o, i) => (
                        <tr key={o.open_id || i} className="border-t border-slate-50">
                          <td className="px-4 py-2 text-slate-600">{o.email}</td>
                          <td className="px-4 py-2 text-right text-xs text-slate-500">
                            {o.opened_at ? new Date(o.opened_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                          </td>
                          <td className="px-4 py-2 text-right text-xs text-slate-400 max-w-[200px] truncate">
                            {(o.user_agent || "").includes("Mobile") ? "Mobile" : (o.user_agent || "").includes("Windows") ? "Desktop" : (o.user_agent || "").substring(0, 30) || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-8">Nenhuma abertura registrada ainda.</p>
              )}
            </div>
          )}

          {/* ── Tab: Cliques ── */}
          {activeTab === "cliques" && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Cliques únicos", value: stats?.unique_clicks ?? 0, icon: ArrowRight },
                  { label: "Total de cliques", value: stats?.total_clicks ?? 0, icon: RefreshCw },
                  { label: "Taxa de cliques", value: `${stats?.click_rate ?? 0}%`, icon: BarChart3 },
                ].map((c, i) => {
                  const I = c.icon;
                  return (
                    <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                      <div className="flex items-start justify-between mb-3">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider leading-tight">{c.label}</p>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ml-2" style={{ backgroundColor: `${tc500}18` }}>
                          <I className="w-4 h-4" style={{ color: tc500 }} />
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-slate-900 leading-none">{c.value}</p>
                    </div>
                  );
                })}
              </div>
              {/* Top URLs */}
              {stats?.top_urls?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-2">Links clicados</p>
                  <div className="space-y-1.5">
                    {stats.top_urls.map((u, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-lg bg-violet-50 px-3 py-2.5">
                        <span className="text-xs font-bold text-violet-400 w-5">{i + 1}.</span>
                        <a href={u.url} target="_blank" rel="noopener noreferrer" className="text-xs text-violet-700 flex-1 truncate underline underline-offset-2 decoration-violet-200 hover:decoration-violet-400">{u.url}</a>
                        <span className="text-xs font-bold text-violet-600">{u.clicks}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {stats?.clicks_timeline?.length > 0 ? (
                <div className="max-h-[250px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500">E-mail</th>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500">URL</th>
                        <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500">Clicado em</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.clicks_timeline.map((c, i) => (
                        <tr key={c.click_id || i} className="border-t border-slate-50">
                          <td className="px-4 py-2 text-slate-600">{c.email}</td>
                          <td className="px-4 py-2 text-xs text-slate-500 max-w-[300px] truncate">{c.url}</td>
                          <td className="px-4 py-2 text-right text-xs text-slate-400">
                            {c.clicked_at ? new Date(c.clicked_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : !stats?.top_urls?.length ? (
                <p className="text-sm text-slate-400 text-center py-8">Nenhum clique registrado ainda.</p>
              ) : null}
            </div>
          )}

          {/* ── Tab: Descadastros ── */}
          {activeTab === "descadastros" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Total descadastros", value: stats?.unsubscribes ?? 0, icon: XCircle },
                  { label: "Taxa de descadastro", value: `${sentCount > 0 ? ((stats?.unsubscribes ?? 0) / sentCount * 100).toFixed(1) : 0}%`, icon: BarChart3 },
                ].map((c, i) => {
                  const I = c.icon;
                  return (
                    <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                      <div className="flex items-start justify-between mb-3">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider leading-tight">{c.label}</p>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ml-2" style={{ backgroundColor: `${tc500}18` }}>
                          <I className="w-4 h-4" style={{ color: tc500 }} />
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-slate-900 leading-none">{c.value}</p>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Contatos que clicaram no link de descadastro são automaticamente removidos de futuros envios. O link é inserido usando a variável <code className="bg-slate-100 px-1 rounded text-[10px]">{"{link_descadastro}"}</code> no editor de modelos.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Schedule Confirm Modal (Metrics) */}
      <AlertDialog open={!!metricsConfirm} onOpenChange={(v) => { if (!v) setMetricsConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {metricsConfirm === "cancel" ? "Cancelar agendamento?" : "Enviar agora?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {metricsConfirm === "cancel"
                ? "A campanha voltará ao status de rascunho e o agendamento será removido."
                : "O agendamento será cancelado e a campanha será enviada imediatamente."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Não, manter</AlertDialogCancel>
            <AlertDialogAction
              className={metricsConfirm === "cancel" ? "bg-rose-500 hover:bg-rose-600" : ""}
              style={metricsConfirm !== "cancel" ? { backgroundColor: tc500 } : {}}
              onClick={async () => {
                const cid = campaign.campaign_id;
                if (metricsConfirm === "cancel") {
                  const res = await fetch(`${API}/email-campaigns/${cid}/cancel-schedule`, { method: "POST", headers: getHeaders() });
                  if (res.ok) { toast.success("Agendamento cancelado"); onBack(); onRefreshList(); }
                } else {
                  const res = await fetch(`${API}/email-campaigns/${cid}/cancel-schedule`, { method: "POST", headers: getHeaders() });
                  if (res.ok) {
                    const sendRes = await fetch(`${API}/email-campaigns/${cid}/send`, { method: "POST", headers: getHeaders() });
                    if (sendRes.ok) { toast.success("Campanha enviada!"); onRefreshList(); }
                  }
                }
                setMetricsConfirm(null);
              }}>
              {metricsConfirm === "cancel" ? "Sim, cancelar" : "Sim, enviar agora"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const PAGE_SIZE = 50;

export default function EmailCampanhasPage() {
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#F43F5E";
  const tc50 = appearance.colors?.["50"] || "#FFF1F2";
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [view, setView] = useState("list");
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [scheduleConfirm, setScheduleConfirm] = useState(null); // { type: "cancel"|"sendNow", campaignId, scheduledAt }
  const [rowSelection, setRowSelection] = useState({});

  // Sort state
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  // Pagination
  const [page, setPage] = useState(0);

  const fetchAll = async () => {
    try {
      const [cRes, tRes] = await Promise.all([
        fetch(`${API}/email-campaigns`, { headers: getHeaders() }),
        fetch(`${API}/email-templates`, { headers: getHeaders() }),
      ]);
      if (cRes.ok) setCampaigns(await cRes.json());
      if (tRes.ok) {
        const all = await tRes.json();
        setTemplates(all.filter(t => t.status === "publicado"));
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { setRowSelection({}); }, [campaigns]);

  const handleCreate = () => { setCurrent(null); setWizardOpen(true); };
  const handleEdit = (c) => {
    if (c.status === "falhou" || c.status === "cancelada" || c.status === "pausada") {
      fetch(`${API}/email-campaigns/${c.campaign_id}`, {
        method: "PUT", headers: getHeaders(), body: JSON.stringify({ status: "rascunho" }),
      }).then(() => fetchAll());
    }
    setCurrent(c);
    setWizardOpen(true);
  };
  const handleView = (c) => { setCurrent(c); setView("detail"); };

  const handleSave = async (data, existingCampaign) => {
    if (existingCampaign?.campaign_id) {
      await fetch(`${API}/email-campaigns/${existingCampaign.campaign_id}`, {
        method: "PUT", headers: getHeaders(), body: JSON.stringify(data),
      });
      fetchAll();
      return { ...existingCampaign, ...data };
    } else {
      const res = await fetch(`${API}/email-campaigns`, {
        method: "POST", headers: getHeaders(), body: JSON.stringify(data),
      });
      const created = await res.json();
      fetchAll();
      return created;
    }
  };

  const handleSend = async (campaignId) => {
    const res = await fetch(`${API}/email-campaigns/${campaignId}/send`, {
      method: "POST", headers: getHeaders(),
    });
    if (res.ok) {
      const result = await res.json();
      toast.success("Campanha sendo enviada...");
      setWizardOpen(false);
      const camp = campaigns.find(c => c.campaign_id === campaignId);
      setCurrent(camp ? { ...camp, status: "enviando", sent_at: new Date().toISOString() } : { campaign_id: campaignId, name: "Campanha", status: "enviando", sent_at: new Date().toISOString() });
      setView("detail");
      fetchAll();
      return result;
    }
    const err = await res.json().catch(() => ({}));
    toast.error(err.detail || "Erro ao enviar.");
    return null;
  };

  const handleDelete = async (id) => {
    await fetch(`${API}/email-campaigns/${id}`, { method: "DELETE", headers: getHeaders() });
    fetchAll();
    toast.success("Campanha excluída!");
  };

  const handleBulkDelete = async () => {
    const ids = Object.keys(rowSelection).filter(k => rowSelection[k]);
    for (const id of ids) {
      await fetch(`${API}/email-campaigns/${id}`, { method: "DELETE", headers: getHeaders() });
    }
    toast.success(`${ids.length} campanha(s) excluída(s)`);
    setRowSelection({});
    fetchAll();
    setBulkDeleteOpen(false);
  };

  // Sort & filter
  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
    setPage(0);
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronsUpDown className="w-3.5 h-3.5 text-slate-300" />;
    return sortDir === "asc" ? <ChevronUp className="w-3.5 h-3.5 text-slate-700" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-700" />;
  };

  const filtered = useMemo(() => {
    let list = [...campaigns];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c => c.name?.toLowerCase().includes(q) || c.subject?.toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      const av = a[sortBy] || "";
      const bv = b[sortBy] || "";
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [campaigns, search, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Columns
  const columns = useMemo(() => [
    {
      id: "select", size: 40, enableSorting: false,
      header: ({ table }) => <Checkbox checked={table.getIsAllPageRowsSelected()} onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)} data-testid="select-all-campaigns" />,
      cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onCheckedChange={(v) => row.toggleSelected(!!v)} onClick={(e) => e.stopPropagation()} data-testid={`select-campaign-${row.original.campaign_id}`} />,
    },
    {
      id: "name", accessorKey: "name", header: "Nome", size: 220,
      cell: ({ row }) => {
        const c = row.original;
        return (
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold" style={{ backgroundColor: tc500 }}>
              {c.name?.charAt(0).toUpperCase() || "C"}
            </div>
            <span className="font-medium text-slate-900 truncate">{c.name}</span>
          </div>
        );
      },
    },
    {
      id: "subject", accessorKey: "subject", header: "Assunto", size: 220,
      cell: ({ getValue }) => <span className="text-slate-500 truncate block max-w-[200px]">{getValue() || "—"}</span>,
    },
    {
      id: "recipient_count", accessorKey: "recipient_count", header: "Destinatários", size: 120,
      cell: ({ getValue }) => <span className="text-slate-600">{getValue() || 0}</span>,
    },
    {
      id: "sent_count", accessorKey: "sent_count", header: "Enviados", size: 100,
      cell: ({ getValue }) => <span className="text-slate-600">{getValue() || 0}</span>,
    },
    {
      id: "status", accessorKey: "status", header: "Status", size: 180,
      cell: ({ row }) => {
        const c = row.original;
        const st = STATUS_MAP[c.status] || STATUS_MAP.rascunho;
        const isClickable = ["falhou", "concluida", "enviando", "pausada", "cancelada", "agendada"].includes(c.status);
        return (
          <div className="flex flex-col gap-0.5">
            {isClickable ? (
              <button onClick={(e) => { e.stopPropagation(); if (c.status !== "agendada") handleView(c); }}
                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${c.status !== "agendada" ? "cursor-pointer hover:opacity-80" : ""} transition-opacity ${st.color}`}
                data-testid={`status-badge-${c.campaign_id}`}>
                {c.status === "enviando" && <Loader2 className="w-3 h-3 animate-spin" />}
                {c.status === "agendada" && <CalendarClock className="w-3 h-3" />}
                {st.label}{c.status === "agendada" && c.scheduled_at ? ` ${new Date(c.scheduled_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}` : ""}
                {isClickable && !["enviando", "agendada"].includes(c.status) && <BarChart3 className="w-3 h-3" />}
              </button>
            ) : (
              <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>
            )}
          </div>
        );
      },
    },
    {
      id: "created_at", accessorKey: "created_at", header: "Criada em", size: 130,
      cell: ({ getValue }) => <span className="text-sm text-slate-500">{getValue() ? new Date(getValue()).toLocaleDateString("pt-BR") : "—"}</span>,
    },
    {
      id: "actions", size: 120, enableSorting: false, header: "",
      cell: ({ row }) => {
        const c = row.original;
        return (
          <div className="flex items-center gap-0.5 justify-end">
            {!["concluida", "enviando"].includes(c.status) && (
              <Tooltip><TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-sky-600"
                  onClick={(e) => { e.stopPropagation(); handleEdit(c); }} data-testid={`edit-campaign-${c.campaign_id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
              </TooltipTrigger><TooltipContent>Editar</TooltipContent></Tooltip>
            )}
            {c.status === "agendada" && (
              <>
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-emerald-600"
                    onClick={(e) => { e.stopPropagation(); setScheduleConfirm({ type: "sendNow", campaignId: c.campaign_id, scheduledAt: c.scheduled_at }); }} data-testid={`send-now-${c.campaign_id}`}>
                    <Send className="w-3.5 h-3.5" />
                  </Button>
                </TooltipTrigger><TooltipContent>Enviar agora</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                    onClick={(e) => { e.stopPropagation(); setScheduleConfirm({ type: "cancel", campaignId: c.campaign_id }); }} data-testid={`cancel-schedule-${c.campaign_id}`}>
                    <XCircle className="w-3.5 h-3.5" />
                  </Button>
                </TooltipTrigger><TooltipContent>Cancelar agendamento</TooltipContent></Tooltip>
              </>
            )}
            {["concluida", "falhou", "enviando", "pausada", "cancelada"].includes(c.status) && (
              <Tooltip><TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600"
                  onClick={(e) => { e.stopPropagation(); handleView(c); }} data-testid={`stats-campaign-${c.campaign_id}`}>
                  <BarChart3 className="w-3.5 h-3.5" />
                </Button>
              </TooltipTrigger><TooltipContent>Métricas</TooltipContent></Tooltip>
            )}
            <Tooltip><TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-rose-500"
                onClick={(e) => { e.stopPropagation(); setDeleteTarget(c.campaign_id); }}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger><TooltipContent>Excluir</TooltipContent></Tooltip>
          </div>
        );
      },
    },
  ], [tc500, campaigns]);

  const [columnVisibility, setColumnVisibility] = useState({ subject: false, created_at: false });
  const toggleableColumns = useMemo(() => columns.filter(c => c.id !== "select" && c.id !== "actions").map(c => ({ id: c.id, label: c.header || c.id })), [columns]);

  const table = useReactTable({
    data: paged,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    state: { rowSelection, columnVisibility },
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: true,
    getRowId: (row) => row.campaign_id,
  });

  const selectedIds = Object.keys(rowSelection).filter(k => rowSelection[k]);
  const hasSelection = selectedIds.length > 0;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <img src="/brand/icon-1.png" alt="Loading" className="w-10 h-10 animate-pulse rounded-xl" />
    </div>
  );

  if (view === "detail" && current) {
    return <CampaignMetrics campaign={current} onBack={() => { setView("list"); setCurrent(null); fetchAll(); }} onRefreshList={fetchAll} tc500={tc500} tc50={tc50} />;
  }

  return (
    <div className="space-y-4" data-testid="email-campanhas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
            <Send className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2" data-testid="email-campanhas-title">
              Campanhas
              <Badge className="bg-white text-slate-500 border border-slate-300 text-sm font-medium px-3 py-1">{campaigns.length}</Badge>
              {hasSelection && (
                <Tooltip><TooltipTrigger asChild>
                  <Badge className="text-white text-sm font-medium px-3 py-1 cursor-default" style={{ backgroundColor: tc500 }}>{selectedIds.length}</Badge>
                </TooltipTrigger><TooltipContent>{selectedIds.length} campanha(s) selecionada(s)</TooltipContent></Tooltip>
              )}
            </h1>
            <p className="text-slate-500 mt-0.5">Envie e-mails em massa para seus contatos</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasSelection && (
            <>
              <Tooltip><TooltipTrigger asChild>
                <Button size="icon" className="h-9 w-9 text-white bg-rose-500 hover:bg-rose-600" onClick={() => setBulkDeleteOpen(true)} data-testid="bulk-delete-campaigns">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger><TooltipContent>Excluir selecionados</TooltipContent></Tooltip>
              <Tooltip><TooltipTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9 text-slate-600" onClick={() => setRowSelection({})}>
                  <X className="w-4 h-4" />
                </Button>
              </TooltipTrigger><TooltipContent>Limpar seleção</TooltipContent></Tooltip>
              <div className="w-px h-6 bg-slate-200" />
            </>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="campaign-search" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} placeholder="Buscar campanha..." className="pl-9 w-full sm:w-56 h-9 text-sm" />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9 text-slate-600" data-testid="column-visibility-btn">
                <Settings2 className="w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 mb-2">Colunas visíveis</p>
              {toggleableColumns.map(col => {
                const isVisible = table.getColumn(col.id)?.getIsVisible() ?? true;
                return (
                  <label key={col.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm text-slate-700">
                    <Checkbox checked={isVisible} onCheckedChange={(v) => table.getColumn(col.id)?.toggleVisibility(!!v)} />
                    {col.label}
                  </label>
                );
              })}
            </PopoverContent>
          </Popover>
          <Tooltip><TooltipTrigger asChild>
            <Button data-testid="create-campaign-btn" onClick={handleCreate} size="icon" style={{ backgroundColor: tc500 }} className="text-white h-9 w-9 hover:opacity-90">
              <Plus className="w-4 h-4" />
            </Button>
          </TooltipTrigger><TooltipContent>Nova Campanha</TooltipContent></Tooltip>
        </div>
      </div>

      {/* Table */}
      {campaigns.length === 0 && !search ? (
        <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Send className="w-8 h-8 text-slate-400" />
          </div>
          <p className="text-slate-600 font-medium">Nenhuma campanha ainda</p>
          <p className="text-slate-400 text-sm mt-1">Crie sua primeira campanha de e-mail</p>
          <Button onClick={handleCreate} className="mt-4 gap-2" variant="outline" data-testid="create-campaign-empty-btn">
            <Plus className="w-4 h-4" /> Nova Campanha
          </Button>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                {table.getHeaderGroups().map(hg => (
                  <tr key={hg.id} className="border-b border-slate-200 bg-slate-50/80">
                    {hg.headers.map(header => {
                      const canSort = header.column.columnDef.enableSorting !== false && header.column.columnDef.accessorKey;
                      return (
                        <th key={header.id}
                          style={{ width: header.getSize() }}
                          className={`px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap ${canSort ? "cursor-pointer select-none hover:text-slate-700" : ""}`}
                          onClick={() => canSort && handleSort(header.column.columnDef.accessorKey)}>
                          <div className="flex items-center gap-1.5">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && <SortIcon col={header.column.columnDef.accessorKey} />}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      {columns.filter(c => table.getColumn(c.id)?.getIsVisible() !== false).map((c, j) => (
                        <td key={j} className="px-3 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" style={{ width: `${40 + Math.random() * 40}%` }} /></td>
                      ))}
                    </tr>
                  ))
                ) : table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-16">
                      <div className="flex flex-col items-center text-slate-400">
                        <Send className="w-10 h-10 mb-3 text-slate-300" />
                        <p className="text-sm">{search ? `Nenhuma campanha encontrada para "${search}"` : "Nenhuma campanha"}</p>
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows.map(row => (
                  <tr key={row.id}
                    data-testid={`campaign-row-${row.original.campaign_id}`}
                    className={`border-b border-slate-100 transition-colors cursor-pointer ${row.getIsSelected() ? "bg-slate-50" : "hover:bg-slate-50/50"}`}
                    onClick={() => handleView(row.original)}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-3 py-2.5">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
            <p className="text-xs text-slate-500">
              {filtered.length > 0 ? `${page * PAGE_SIZE + 1}-${Math.min((page + 1) * PAGE_SIZE, filtered.length)} de ${filtered.length}` : "Nenhum resultado"}
            </p>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="prev-page-btn">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i;
                else if (page < 3) pageNum = i;
                else if (page > totalPages - 4) pageNum = totalPages - 5 + i;
                else pageNum = page - 2 + i;
                return (
                  <Button key={pageNum} variant={pageNum === page ? "default" : "outline"} size="sm"
                    className={`h-8 w-8 p-0 text-xs ${pageNum === page ? "text-white" : ""}`}
                    style={pageNum === page ? { backgroundColor: tc500 } : {}}
                    onClick={() => setPage(pageNum)}>
                    {pageNum + 1}
                  </Button>
                );
              })}
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="next-page-btn">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete modal */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Excluir campanha</DialogTitle>
            <DialogDescription>Tem certeza que deseja excluir esta campanha? Esta ação não pode ser desfeita.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button variant="destructive" data-testid="confirm-delete-campaign" onClick={() => { handleDelete(deleteTarget); setDeleteTarget(null); }}>Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2 text-rose-600">
              <Trash2 className="w-5 h-5" /> Excluir {selectedIds.length} campanha(s)
            </AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita. Todas as campanhas selecionadas serão permanentemente excluídas.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-500 hover:bg-rose-600" onClick={handleBulkDelete}>Excluir {selectedIds.length}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Schedule Confirmation Modal */}
      <AlertDialog open={!!scheduleConfirm} onOpenChange={(v) => { if (!v) setScheduleConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {scheduleConfirm?.type === "cancel" ? "Cancelar agendamento?" : "Enviar agora?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {scheduleConfirm?.type === "cancel"
                ? "A campanha voltará ao status de rascunho e o agendamento será removido."
                : "O agendamento será cancelado e a campanha será enviada imediatamente."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Não, manter</AlertDialogCancel>
            <AlertDialogAction
              className={scheduleConfirm?.type === "cancel" ? "bg-rose-500 hover:bg-rose-600" : ""}
              style={scheduleConfirm?.type !== "cancel" ? { backgroundColor: tc500 } : {}}
              onClick={async () => {
                const cid = scheduleConfirm?.campaignId;
                if (!cid) return;
                if (scheduleConfirm.type === "cancel") {
                  const res = await fetch(`${API}/email-campaigns/${cid}/cancel-schedule`, { method: "POST", headers: getHeaders() });
                  if (res.ok) { toast.success("Agendamento cancelado"); fetchAll(); }
                  else toast.error("Erro ao cancelar");
                } else {
                  const res = await fetch(`${API}/email-campaigns/${cid}/cancel-schedule`, { method: "POST", headers: getHeaders() });
                  if (res.ok) { handleSend(cid); }
                }
                setScheduleConfirm(null);
              }}>
              {scheduleConfirm?.type === "cancel" ? "Sim, cancelar" : "Sim, enviar agora"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Wizard Modal */}
      <CampaignWizardModal
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        campaign={current}
        templates={templates}
        onSave={handleSave}
        onSend={handleSend}
        onDone={() => { setWizardOpen(false); fetchAll(); }}
        tc500={tc500}
        tc50={tc50}
      />
    </div>
  );
}
